import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import { PAGES } from "../../enums/PagesEnum";
import {
  addNewfdpInBulk,
  getfdpList,
  updateFdpStatus,
} from "../../app/features/fdp/fdpSlice";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import {
  FDP_COLUMN_NAME,
  FDP_LABEL_NAME,
  FdpExpectedColumns,
} from "./FdpConstants";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import * as XLSX from "xlsx";
import AddIcon from "@mui/icons-material/Add";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import {
  DownloadFileModel,
  convertArrayToCSV,
  downloadCsv,
  downloadXlsx,
  prepareAndDownloadFile,
} from "../../helpers/downloadFile";
import FdpDataFilter from "./FdpDataFilter";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import AddFdpModal from "./Modals/AddFdpModal";
import FdpDetailsModal from "./Modals/FdpDetailsModal";
import UploadIcon from "@mui/icons-material/Upload";
import { FdpResponse } from "../../app/models/responseModels/FdpResponse";
import FileUploadModal from "../../components/common/modalPopup/FileUploadModal";
import { BulkFdpRequest } from "../../app/models/requestModels/fdpRequest";
import { formatISODateString } from "../../components/common/utils/DateUtils";

const breadcrumb = ["FDP"];

function BulkFdpAddRequestModel(fdps: any[]): BulkFdpRequest {
  return {
    fdps: fdps,
  };
}

const Fdp = () => {
  const dispatch = useAppDispatch();
  const { isFdpLoading, fdpList } = useSelector(
    (state: RootState) => state.fdp
  );

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.FDP
    )
  );
  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );
  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [filteredData, setFilteredData] = useState<FdpResponse[]>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [isTableLoading, setTableLoading] = useState<boolean>(false);

  // ***** Callbacks *****
  const getFdpData = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await dispatch(getfdpList()).unwrap();
      if (response.isSuccess) {
        setTableLoading(false);
      }
      if (!response.isSuccess) {
        setTableLoading(false);
        toast.error(response.message!, {
          toastId: "fdpList",
        });
      }
    } catch (error) {
      setTableLoading(false);
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getFdpData();
    }
    return () => {
      isFunctionRan = false;
    };
  }, [getFdpData]);

  useEffect(() => {
    if (fdpList.length > 0) {
      if (filteredData.length === fdpList.length) {
        setFilteredData(fdpList);
      }
    }
  }, [fdpList, filteredData]);

  useEffect(() => {
    if (
      selectedRows.length > 0 &&
      selectedRows.some(
        (row: any) => row.original.active !== selectedRows[0].original.active
      )
    ) {
      toast.info("Either select Active or Inactive Rows!", {
        toastId: "mixedSelectionException",
      });
    }
  }, [selectedRows]);

  // ***** Handlers *****
  const handleRefresh = () => {
    getFdpData();
  };

  // ***** Handlers *****
  const handleResetAndRefresh = (table: MRT_TableInstance<FdpResponse>) => {
    table.resetRowSelection(true);
    handleRefresh();
  };

  const handleActiveStatus = (
    table: MRT_TableInstance<FdpResponse>,
    activeStatus: boolean
  ) => {
    let selectedRowIds: string[] = [];
    table.getSelectedRowModel().flatRows.map((row) => {
      selectedRowIds.push(row.original.id + "");
    });
    updateActiveStatus(selectedRowIds, activeStatus, table);
  };
  // ***** Handlers *****
  const updateActiveStatus = async (
    selectedRowIds: string[],
    activeStatus: boolean,
    table: MRT_TableInstance<FdpResponse>
  ) => {
    try {
      const response = await dispatch(
        updateFdpStatus({ fdpIds: selectedRowIds, activeStatus: activeStatus })
      ).unwrap();
      if (response.isSuccess) {
        handleResetAndRefresh(table);
        toast.success(response.message!, {
          toastId: "UpdateFdpStatusSuccess",
        });
      } else {
        toast.error(response.message!, {
          toastId: "UpdateFdpStatusError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "UpdateFdpStatusException",
      });
    }
    return true;
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const disableActivateButton = (table: MRT_TableInstance<FdpResponse>) => {
    const selectedRows = table.getSelectedRowModel().flatRows;
    if (selectedRows.length === 0) {
      return true;
    }
    for (const row of selectedRows) {
      if (row.original.active === true) {
        return true;
      }
    }
    return false;
  };

  const disableDeactivateButton = (table: MRT_TableInstance<FdpResponse>) => {
    const selectedRows = table.getSelectedRowModel().flatRows;
    if (selectedRows.length === 0) {
      return true;
    }
    for (const row of selectedRows) {
      if (row.original.active === false) {
        return true;
      }
    }
    return false;
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<FdpResponse>[]>(
    () => [
      {
        accessorKey: FDP_COLUMN_NAME.ID,
        header: FDP_LABEL_NAME.ID,
        size: 10,
      },
      {
        accessorKey: FDP_COLUMN_NAME.COMAPANY_NAME,
        header: FDP_LABEL_NAME.COMAPANY_NAME,
        size: 100,
      },
      {
        accessorKey: FDP_COLUMN_NAME.ACTIVE,
        header: FDP_LABEL_NAME.ACTIVE,
        size: 50,
        Cell: ({ row }) => (
          <div>
            {row.original.active ? <span>True</span> : <span>False</span>}
          </div>
        ),
      },
      {
        accessorKey: FDP_COLUMN_NAME.DISPATCHER_NAME,
        header: FDP_LABEL_NAME.DISPATCHER_NAME,
        size: 80,
      },
      {
        accessorKey: FDP_COLUMN_NAME.EMAIL,
        header: FDP_LABEL_NAME.EMAIL,
        size: 150,
      },
      {
        accessorKey: FDP_COLUMN_NAME.POINT_OF_CONTACT,
        header: FDP_LABEL_NAME.POINT_OF_CONTACT,
        size: 80,
      },
      {
        accessorKey: FDP_COLUMN_NAME.PRIMARY_MSA,
        header: FDP_LABEL_NAME.PRIMARY_MSA,
        size: 80,
      },
      {
        accessorKey: FDP_COLUMN_NAME.SECONDARY_MSA,
        header: FDP_LABEL_NAME.SECONDARY_MSA,
        size: 80,
      },
      {
        accessorKey: FDP_COLUMN_NAME.ADDRESS,
        header: FDP_LABEL_NAME.ADDRESS,
        size: 150,
      },
      {
        accessorKey: FDP_COLUMN_NAME.CITY,
        header: FDP_LABEL_NAME.CITY,
        size: 80,
      },
      {
        accessorKey: FDP_COLUMN_NAME.STATE,
        header: FDP_LABEL_NAME.STATE,
        size: 80,
      },
      {
        accessorKey: FDP_COLUMN_NAME.ZIP_CODE,
        header: FDP_LABEL_NAME.ZIP_CODE,
        size: 80,
      },
      {
        accessorKey: FDP_COLUMN_NAME.PRIMARY_PHONE,
        header: FDP_LABEL_NAME.PRIMARY_PHONE,
      },
      {
        accessorKey: FDP_COLUMN_NAME.SECONDARY_PHONE,
        header: FDP_LABEL_NAME.SECONDARY_PHONE,
      },
      {
        accessorKey: FDP_COLUMN_NAME.CREATED_ON,
        header: FDP_LABEL_NAME.CREATED_ON,
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
      {
        accessorKey: FDP_COLUMN_NAME.UPDATED_ON,
        header: FDP_LABEL_NAME.UPDATED_ON,
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          if (!value) return "N/A";
          const formattedDate = formatISODateString(value);
          return formattedDate;
        },
      },
    ],
    []
  );

  const handleBulkAddFdps = async (validData: any[], invalidData: any[]) => {
    try {
      const invalidFileData = invalidData.map((item) => ({
        ...item.data,
        error: item.error, // Add the 'Error' field to each item
      }));
      if (validData.length === 0) {
        prepareAndDownloadFile(invalidFileData, "errorFdpCreation_res");
        return false;
      }
      const bulkFdpData = BulkFdpAddRequestModel(validData);
      const response = await dispatch(addNewfdpInBulk(bulkFdpData)).unwrap();
      if (response.isSuccess) {
        if (invalidData.length) {
          prepareAndDownloadFile(invalidFileData, "errorFdpCreation_res");
          toast.warning("Some FDPs couldn't save, Please check the file!", {
            toastId: "AddBulkFdpException",
          });
          return true;
        }
        toast.success(response.message, {
          toastId: "AddBulkFdpSuccess",
        });
      } else {
        if (response.data && response.data.length) {
          const requiredKeys = FdpExpectedColumns.map(
            (item) => item.camelCaseName
          );
          requiredKeys.push("error");
          // Filter response data to keep only keys present in invalidFileData
          const filteredResponseData = response.data.map((item: any) => {
            const filteredItem: any = {};
            requiredKeys.forEach((key) => {
              if (item.hasOwnProperty(key)) {
                filteredItem[key] = item[key];
              }
            });
            return filteredItem;
          });
          // Combine invalidFileData and filteredResponseData
          const mergedData = [...invalidFileData, ...filteredResponseData];
          prepareAndDownloadFile(mergedData, "errorFdpCreation_res");
          toast.error(response.message, {
            toastId: "AddBulkFdpException",
          });
          return true;
        }
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "AddBulkFdpException",
      });
    }
    return true;
  };

  const [addFdpModal, openAddFdpModal] = useSubmitModal((closeAddFdpModal) => (
    <AddFdpModal
      closeAddFdpModal={closeAddFdpModal}
      onCompleted={() => getFdpData()}
    />
  ));

  const [fdpDetailsModal, openFdpDetailsModal] =
    useSubmitModalWithParameter<FdpResponse>(
      (fdpRowData, closeFdpDetailsModal) => (
        <FdpDetailsModal
          closeFdpDetailsModal={closeFdpDetailsModal}
          onCompleted={getFdpData}
          fdpRowData={fdpRowData}
        />
      )
    );

  const [fileUploadModal, openFileUploadModal] = useSubmitModal(
    (closeFileUploadModal) => (
      <FileUploadModal
        closeFileUploadModal={closeFileUploadModal}
        expectedColumns={FdpExpectedColumns}
        asyncSubmitOperation={handleBulkAddFdps}
        onCompleted={getFdpData}
        sampleFileName={"Bulk_FDPs_Sample.xlsx"}
      />
    )
  );

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      // Transform filteredData into CSV format
      const csvData = getCsvFilteredDataFileMapping();
      // Convert CSV data to CSV string
      const csvContent = convertArrayToCSV(csvData);
      // Download the CSV
      const file: DownloadFileModel = {
        data: csvContent,
        contentType: "text/csv",
        fileName: "FdpData.csv",
      };
      downloadCsv(file);
    } catch (error) {
      toast.error("unable to download file", {
        toastId: "FdpDownloadCsv",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const fileData = getFilteredDataFileMapping();
      // Convert data to worksheet
      const worksheet = XLSX.utils.json_to_sheet(fileData);
      // Create a workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "FdpData");
      // Convert the workbook to a binary XLSX object
      const wbArray = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      // Download the XLSX file
      downloadXlsx(blob, "FdpData.xlsx");
    } catch (error) {
      toast.error("unable to download file", {
        toastId: "FdpDownloadXlsx",
      });
    }
  };

  const getFilteredDataFileMapping = () => {
    return filteredData.map((item: FdpResponse) => {
      return {
        ID: item.id,
        CompanyName: item.companyName,
        DispatcherName: item.dispatcherName,
        ActiveStatus: item.active,
        Email: item.email,
        PointOfContact: item.pointOfContact,
        PrimaryPhone: item.primaryPhone,
        SecondaryPhone: item.secondaryPhone,
        PrimaryMSA: item.primaryMsa,
        SecondaryMSA: item.secondaryMsa,
        Address: item.address,
        City: item.city,
        State: item.state,
        Zipcode: item.zipCode,
        CreatedOn: item.createdOn,
        UpdatedOn: item.updatedOn,
      };
    });
  };

  const getCsvFilteredDataFileMapping = () => {
    return filteredData.map((item: FdpResponse) => {
      return {
        ID: item.id,
        CompanyName: item.companyName ? `"${item.companyName}"` : "",
        ActiveStatus: item.active,
        DispatcherName: item.dispatcherName,
        Email: item.email ? `"${item.email}"` : "",
        PointOfContact: item.pointOfContact,
        PrimaryPhone: item.primaryPhone,
        SecondaryPhone: item.secondaryPhone,
        PrimaryMSA: item.primaryMsa,
        SecondaryMSA: item.secondaryMsa,
        Address: item.address ? `"${item.address}"` : "",
        City: item.city,
        State: item.state,
        Zipcode: item.zipCode,
        CreatedOn: `"${item.createdOn}"`,
        UpdatedOn: `"${item.updatedOn}"`,
      };
    });
  };
  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<FdpResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={() => handleResetAndRefresh(table)}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Upload/Import">
          <IconButton type="button" onClick={openFileUploadModal}>
            <UploadIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <span>
            <IconButton
              disabled={filteredData.length === 0}
              type="button"
              onClick={handleDownloadMenuClick}
            >
              <DownloadIcon sx={{ color: "primary.main" }} />
            </IconButton>
          </span>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            CSV
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            EXCEL
          </MenuItem>
        </Menu>
        {IsCreatePermission && IsAdmin && (
          <CustomTooltip arrow title="New FDP">
            <span>
              <IconButton
                disabled={isFdpLoading}
                type="button"
                onClick={openAddFdpModal}
              >
                <AddIcon sx={{ color: "primary.main" }} />
              </IconButton>
            </span>
          </CustomTooltip>
        )}
        {IsCreatePermission && IsAdmin && (
          <Button
            color="success"
            disabled={disableActivateButton(table)}
            onClick={() => handleActiveStatus(table, true)}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            ACTIVATE
          </Button>
        )}
        {IsCreatePermission && IsAdmin && (
          <Button
            color="warning"
            disabled={disableDeactivateButton(table)}
            onClick={() => handleActiveStatus(table, false)}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            DEACTIVATE
          </Button>
        )}
      </Box>
    );
  };
  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <FdpDataFilter
          fdpData={fdpList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isFdpLoading || isTableLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No FDPs"}
        openModal={openFdpDetailsModal}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={false}
        defaultSortColumnName={"id"}
        checkBoxSelection={true}
        setSelectedRows={setSelectedRows}
      />
      {fdpDetailsModal}
      {addFdpModal}
      {fileUploadModal}
    </Box>
  );
};

export default Fdp;
